<template>
    <section class="presentation_view_wrp">
        <div class="pdf_viewer_popup pdfViewMain">

            <!-- Loader -->
            <div
                v-if="viewerLoader"
                class="viewer_loader"
            >
                Loading PDF...
            </div>

            <!-- Error -->
            <div
                v-else-if="documentError"
                class="viewer_error"
            >
                {{ documentError }}
            </div>

            <!-- PDF Viewer -->
            <div
                v-else
                class="viewer_body"
            >
                <PDFViewer
                    v-if="url"
                    :url="url"
                    @document-errored="onDocumentErrored"
                />
            </div>

        </div>
    </section>
</template>

<script>
import axios from "axios";
import PDFViewer from "@/components/common/pdfViewer/PDFViewer.vue";

export default {
    name: "PresentationVersionUpgrade",

    components: {
        PDFViewer
    },

    data() {
        return {
            url: "",
            pdfViewerFileNAME: "",
            documentError: "",
            viewerLoader: false,

            requestSource: null,

            watermark: {
                disclaimer:
                    "This PDF is for preview purposes only. " +
                    "<br /> Do not share, copy, distribute, or reproduce this document.",
                date: new Date().toLocaleString()
            }
        };
    },

    mounted() {
        this.addPresentationBodyClass();
        this.addSecurityEvents();
        this.getPresentationPdf();
    },

    beforeDestroy() {
        this.cleanupComponent();
    },

    beforeRouteLeave(to, from, next) {
        this.cleanupComponent();
        next();
    },

    methods: {
        //----------------------------------------------------
        // LOAD PDF
        //----------------------------------------------------

        async getPresentationPdf() {
            this.viewerLoader = true;
            this.documentError = "";

            this.revokePdfUrl();

            this.requestSource = axios.CancelToken.source();

            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_API_BASE_URL}api/account/user/presentation/`,
                    {
                        params: {
                            title: "Lymphomap_Discover_Us_New"
                        },
                        responseType: "blob",
                        cancelToken: this.requestSource.token
                    }
                );

                if (!response.data || response.data.size === 0) {
                    throw new Error("The PDF file is empty.");
                }

                const contentType =
                    response.headers &&
                    response.headers["content-type"];

                if (
                    contentType &&
                    contentType.indexOf("application/pdf") === -1
                ) {
                    const apiErrorMessage =
                        await this.getBlobErrorMessage(response.data);

                    throw new Error(
                        apiErrorMessage ||
                        "The server did not return a valid PDF file."
                    );
                }

                const pdfBlob = new Blob(
                    [response.data],
                    {
                        type: "application/pdf"
                    }
                );

                this.url = URL.createObjectURL(pdfBlob);

                this.pdfViewerFileNAME =
                    "Lymphomap_Discover_Us_New.pdf";
            }
            catch (error) {
                if (axios.isCancel(error)) {
                    return;
                }

                console.error(
                    "Unable to load presentation PDF:",
                    error
                );

                this.documentError =
                    await this.getPdfErrorMessage(error);
            }
            finally {
                this.viewerLoader = false;
                this.requestSource = null;
            }
        },

        //----------------------------------------------------
        // API ERROR HANDLING
        //----------------------------------------------------

        async getPdfErrorMessage(error) {
            if (!error) {
                return "Unable to load PDF.";
            }

            if (error.message) {
                if (
                    error.message !== "Network Error" &&
                    error.message !== "Request failed"
                ) {
                    return error.message;
                }
            }

            if (
                error.response &&
                error.response.data
            ) {
                const responseMessage =
                    await this.getBlobErrorMessage(
                        error.response.data
                    );

                if (responseMessage) {
                    return responseMessage;
                }
            }

            if (
                error.response &&
                error.response.status === 401
            ) {
                return "Your session has expired. Please sign in again.";
            }

            if (
                error.response &&
                error.response.status === 403
            ) {
                return "You do not have permission to view this document.";
            }

            if (
                error.response &&
                error.response.status === 404
            ) {
                return "The requested PDF document was not found.";
            }

            if (
                error.response &&
                error.response.status >= 500
            ) {
                return "The server is currently unable to load the PDF.";
            }

            if (error.message === "Network Error") {
                return "Network error. Please check your internet connection.";
            }

            return "Unable to load PDF.";
        },

        async getBlobErrorMessage(data) {
            if (!data) {
                return "";
            }

            if (typeof data === "string") {
                return data;
            }

            if (!(data instanceof Blob)) {
                return (
                    data.message ||
                    data.detail ||
                    data.error ||
                    ""
                );
            }

            try {
                const errorText = await this.readBlobAsText(data);

                if (!errorText) {
                    return "";
                }

                try {
                    const parsedError = JSON.parse(errorText);

                    return (
                        parsedError.message ||
                        parsedError.detail ||
                        parsedError.error ||
                        ""
                    );
                }
                catch (parseError) {
                    return errorText;
                }
            }
            catch (error) {
                return "";
            }
        },

        readBlobAsText(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = () => {
                    resolve(reader.result);
                };

                reader.onerror = () => {
                    reject(reader.error);
                };

                reader.readAsText(blob);
            });
        },

        //----------------------------------------------------
        // BODY CLASS
        //----------------------------------------------------

        addPresentationBodyClass() {
            document.body.classList.add(
                "presentation_view"
            );
        },

        removePresentationBodyClass() {
            document.body.classList.remove(
                "presentation_view"
            );
        },

        //----------------------------------------------------
        // SECURITY EVENTS
        //----------------------------------------------------

        addSecurityEvents() {
            document.addEventListener(
                "contextmenu",
                this.disableContextMenu
            );

            document.addEventListener(
                "keydown",
                this.disableShortcuts,
                true
            );

            document.addEventListener(
                "dragstart",
                this.disableDrag
            );

            document.addEventListener(
                "selectstart",
                this.disableSelection
            );
        },

        removeSecurityEvents() {
            document.removeEventListener(
                "contextmenu",
                this.disableContextMenu
            );

            document.removeEventListener(
                "keydown",
                this.disableShortcuts,
                true
            );

            document.removeEventListener(
                "dragstart",
                this.disableDrag
            );

            document.removeEventListener(
                "selectstart",
                this.disableSelection
            );
        },

        //----------------------------------------------------
        // DISABLE EVENTS
        //----------------------------------------------------

        disableContextMenu(event) {
            event.preventDefault();
        },

        disableDrag(event) {
            event.preventDefault();
        },

        disableSelection(event) {
            event.preventDefault();
        },

        //----------------------------------------------------
        // BLOCK BROWSER SHORTCUTS
        //----------------------------------------------------

        disableShortcuts(event) {
            const key = event.key
                ? event.key.toLowerCase()
                : "";

            const hasControlKey =
                event.ctrlKey || event.metaKey;

            const blockedShortcut =
                (hasControlKey && key === "s") ||
                (hasControlKey && key === "p") ||
                (hasControlKey && key === "u") ||
                (hasControlKey && key === "o") ||
                (
                    hasControlKey &&
                    event.shiftKey &&
                    key === "i"
                ) ||
                (
                    hasControlKey &&
                    event.shiftKey &&
                    key === "j"
                ) ||
                (
                    hasControlKey &&
                    event.shiftKey &&
                    key === "c"
                ) ||
                (
                    hasControlKey &&
                    event.shiftKey &&
                    key === "k"
                ) ||
                (
                    hasControlKey &&
                    event.shiftKey &&
                    key === "p"
                ) ||
                key === "f12";

            if (!blockedShortcut) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        },

        //----------------------------------------------------
        // PDF VIEWER CALLBACK
        //----------------------------------------------------

        onDocumentErrored(error) {
            console.error(
                "PDF viewer document error:",
                error
            );

            if (error && error.text) {
                this.documentError = error.text;
                return;
            }

            if (error && error.message) {
                this.documentError = error.message;
                return;
            }

            this.documentError =
                "Unable to load document.";
        },

        //----------------------------------------------------
        // CLEANUP
        //----------------------------------------------------

        cancelPdfRequest() {
            if (!this.requestSource) {
                return;
            }

            this.requestSource.cancel(
                "Presentation PDF request cancelled."
            );

            this.requestSource = null;
        },

        revokePdfUrl() {
            if (!this.url) {
                return;
            }

            URL.revokeObjectURL(this.url);
            this.url = "";
        },

        cleanupComponent() {
            this.removePresentationBodyClass();
            this.removeSecurityEvents();
            this.cancelPdfRequest();
            this.revokePdfUrl();
        }
    }
};
</script>

<style lang="scss" scoped>
.presentation_view_wrp {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;

    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}

.pdf_viewer_popup {
    width: 100%;
    height: 100%;
}

.viewer_body {
    width: 100%;
    height: 100%;
    overflow: auto;
}

.viewer_loader,
.viewer_error {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 80vh;
    padding: 20px;
    text-align: center;
}

.viewer_loader {
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 1px;
}

.viewer_error {
    color: red;
    font-size: 18px;
    font-weight: 500;
}
</style>